import { looks, tapasPosters, instagramPosts } from '../../data/home';
import { addressLine, business, hoursDisplay } from '../../data/business';
import { useOpenStatus } from '../../utils/openStatus';
import Caption from './Caption';
import { PhoneIcon } from './icons';
import { ArrowLink, CallLink, OrderLink } from './links';
import useFitText from './useFitText';

/** hoursDisplay runs Monday-first; JS weekdays count from Sunday. */
const weekdayIndex = (i: number) => (i + 1) % 7;

const RAIL_OFFSET = 3;

export const SpMenuRail = () => (
  <section className="sp-rail" aria-labelledby="sp-rail-h">
    <div className="sp-wrap">
      <div className="sp-rail-head">
        <h2 className="sp-h1" id="sp-rail-h">Also on the menu</h2>
        <ArrowLink to="/menu" track="menu_click">The full menu</ArrowLink>
      </div>
      <div className="sp-rail-grid">
        {looks.slice(RAIL_OFFSET, RAIL_OFFSET + 3).map((look) => (
          <article className="sp-rail-item" key={look.menuId}>
            <div className="sp-rail-photo">
              <img
                src={look.image.src}
                alt={look.image.alt}
                loading="lazy"
                decoding="async"
                style={{ objectPosition: look.image.posM ?? '50% 50%' }}
              />
            </div>
            <Caption look={look} compact as="h3" />
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const SpStory = () => (
  <section className="sp-story" id="intro" aria-labelledby="sp-story-h">
    <div className="sp-wrap">
      <div className="sp-story-grid">
        <div className="sp-story-copy">
          <h2 className="sp-display" id="sp-story-h">Passed down through generations</h2>
          <p>
            At Thaprachan Thai, we pride ourselves on being one of the best Thai restaurants in Seattle. Our recipes
            have been passed down through generations, preserving authentic tastes while adding our own modern twist
            that captivates diners from all across the city.
          </p>
          <p>
            Conveniently located near the University of Washington, we&rsquo;re the perfect spot for students,
            faculty, and locals to enjoy exceptional Thai food.
          </p>
          <ArrowLink to="/near-uw">Near UW</ArrowLink>
        </div>
        <figure className="sp-story-photo">
          <div className="sp-frame">
            <img
              src="/images/menu-photos/optimized/ChefImage.webp"
              alt="Chef preparing authentic Thai cuisine at a flaming wok"
              loading="lazy"
              decoding="async"
              width={1200}
              height={800}
            />
          </div>
          <figcaption>
            <b>Chef Signature</b>
            <span>Our head chef brings 25 years of culinary expertise to Seattle.</span>
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
);

const POSTER_CLASSES = ['sp-p1', 'sp-p2', 'sp-p3', 'sp-p4', 'sp-p5', 'sp-p6'];

export const SpTapas = () => (
  <section className="sp-tapas" id="tapas" aria-labelledby="sp-tapas-h">
    <div className="sp-wrap">
      <div className="sp-tapas-grid">
        <div className="sp-tapas-head">
          <h2 className="sp-display" id="sp-tapas-h">Tapas is back</h2>
          <p className="sp-tapas-back">Thai-inspired small plates, made for sharing.</p>
          <p>Our tapas lineup has returned.</p>
        </div>
        {tapasPosters.map((poster, i) => (
          <figure className={`sp-poster ${POSTER_CLASSES[i]}`} key={poster.name}>
            <img
              src={poster.src}
              alt={`Hand-lettered poster for ${poster.name}: ${poster.line}`}
              width={1081}
              height={1400}
              loading="lazy"
              decoding="async"
            />
            <figcaption>
              <b>{poster.name}</b>
              <span>{poster.line}</span>
            </figcaption>
          </figure>
        ))}
        <div className="sp-tapas-foot">
          <ArrowLink to="/blog/thai-tapas-menu">Read about the tapas</ArrowLink>
        </div>
      </div>
    </div>
  </section>
);

export const SpVisit = () => {
  const status = useOpenStatus();
  const address = useFitText<HTMLHeadingElement>(260);
  return (
    <section className="sp-visit sp-dark" id="locations" aria-labelledby="sp-visit-h">
      <div className="sp-wrap">
        <div className="sp-visit-top">
          <h2 className="sp-visit-address" id="sp-visit-h" ref={address}>
            <span>{business.address.street}</span>
          </h2>
          <p className="sp-visit-where">
            {business.address.locality}, {business.address.region} {business.address.postalCode}. Just a 10-minute
            drive from the University of Washington. Call to make a reservation.
          </p>
        </div>
        <div className="sp-visit-grid">
          <div className="sp-visit-left">
            <div aria-live="polite">
              <p className={`sp-status is-${status.state}`}>
                <span className="sp-dot" aria-hidden="true" />
                <span>{status.headline}</span>
              </p>
              <p className="sp-status-sub">{status.detail}</p>
            </div>
            <ol className="sp-hours" aria-label="Opening hours">
              {hoursDisplay.map((row, i) => {
                const today = weekdayIndex(i) === status.day;
                return (
                  <li key={row.day} className={today ? 'is-today' : undefined} aria-current={today ? 'date' : undefined}>
                    <span className="sp-day">{row.day}</span>
                    <span className="sp-time">{row.time}</span>
                    <span className="sp-today">Today</span>
                  </li>
                );
              })}
            </ol>
            <div className="sp-visit-actions">
              <OrderLink />
              <CallLink className="sp-btn sp-btn--secondary">
                <PhoneIcon />
                <span className="sp-num">{business.telephoneDisplay}</span>
              </CallLink>
            </div>
          </div>
          <div className="sp-visit-right">
            <div className="sp-map">
              <span className="sp-map-fallback">Map of {business.address.street}</span>
              <iframe
                title={`Map: ${business.name}, ${addressLine}`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(addressLine)}&z=15&output=embed`}
              />
            </div>
            <div className="sp-map-cap">
              <a className="sp-map-link" href={business.mapLink} target="_blank" rel="noopener noreferrer" data-track="directions_click">
                <i className="sp-pin" aria-hidden="true" />
                <span>Directions in Google Maps</span>
              </a>
              <span>{business.address.locality}, {business.address.region} {business.address.postalCode}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const SpCatering = () => (
  <section className="sp-catering" id="catering" aria-labelledby="sp-cat-h">
    <div className="sp-wrap">
      <div className="sp-cat-grid">
        <figure className="sp-cat-photo">
          <img
            src={encodeURI('/images/Menu Photos /optimized/Catering.webp')}
            alt="Catering trays seen from above: red curry, stir-fried vegetables, fried rice and sauces"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <div className="sp-cat-copy">
          <h2 className="sp-h1" id="sp-cat-h">Full-Service Catering</h2>
          <p className="sp-cat-sub">For events from 10 to 200 guests</p>
          <p>
            Bring the exquisite flavors of Thaprachan Thai to your next event. Our catering services are perfect for
            corporate gatherings, weddings, family celebrations, and more.
          </p>
          <ul className="sp-cat-list">
            <li>Customized menus to suit your preferences and dietary needs</li>
            <li>Professional staff including servers and chefs</li>
            <li>Elegant food presentation and setup</li>
            <li>Delivery, setup, and cleanup services</li>
          </ul>
          <div className="sp-cat-ctas">
            <OrderLink>Order Catering Now</OrderLink>
            <ArrowLink to={business.telephoneHref} track="call_click">
              <span className="sp-num">Call {business.telephoneDisplay}</span>
            </ArrowLink>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const SpSocial = () => (
  <section className="sp-social" id="social" aria-labelledby="sp-social-h">
    <div className="sp-wrap">
      <div className="sp-social-inner">
        <div className="sp-social-head">
          <h2 className="sp-h2" id="sp-social-h">On Instagram</h2>
          <ArrowLink to={business.sameAs[0]} external>@thaprachanthai</ArrowLink>
        </div>
        <div className="sp-social-grid">
          {instagramPosts.map((post) => (
            <a key={post.href} href={post.href} target="_blank" rel="noopener noreferrer" aria-label={post.label}>
              <img src={post.src} alt="" loading="lazy" decoding="async" width={640} height={640} />
            </a>
          ))}
        </div>
      </div>
    </div>
  </section>
);
